import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { Column } from 'react-table';
import { Button, Table } from '@nbirl/nbi-common-ui-libs';

import { useErrorToast } from '../../../../../shared/hooks';
import { SecureDocument } from '../../models';
import { useSharedDocumentQuery } from '../../queries';
import { getDocumentRowCells, getDocumentRowProps } from '../../utils';

type SharedDocumentTableProps = {
  userId: string;
  documentId: string;
  columns: Column<any>[];
  documentData?: SecureDocument;
  documentListRowCells?: NodeListOf<HTMLTableCellElement>;
};

export const SharedDocumentTable = ({
  userId,
  documentId,
  columns,
  documentData,
  documentListRowCells,
}: SharedDocumentTableProps) => {
  const history = useHistory();
  const { showErrorToast } = useErrorToast();
  const sharedDocumentQuery = useSharedDocumentQuery(documentId, userId, {
    onError: () => {
      showErrorToast(
        "The document that was shared with you couldn't be found."
      );
    },
  });
  const data = (documentData && [documentData]) || [];

  const handleDismiss = () => {
    history.replace(window.location.pathname);
  };

  const sharedContainerRef = useCallback(
    (el: HTMLDivElement) => {
      if (el) {
        const sharedDocumentsRowCells = getDocumentRowCells(el);

        if (!sharedDocumentsRowCells) {
          return;
        }

        documentListRowCells?.forEach((cell, key) => {
          sharedDocumentsRowCells[key].style.width = cell.offsetWidth + 'px';
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentListRowCells, columns]
  );

  return (
    <>
      {sharedDocumentQuery.isSuccess &&
        sharedDocumentQuery.data &&
        documentData && (
          <div className="bg-primary-50 py-5">
            <div className="flex justify-between mx-6 text-teal-500">
              <div>{sharedDocumentQuery.data?.sharedBy} shared with you</div>
              <Button variant="ghost" onClick={handleDismiss}>
                Dismiss
              </Button>
            </div>

            <div ref={sharedContainerRef}>
              <Table
                className="min-h-0"
                removeMinHeight
                showHeading={false}
                getRowProps={getDocumentRowProps}
                columns={columns}
                data={data}
                totalCount={data.length}
                showPagination={false}
              />
            </div>
          </div>
        )}
    </>
  );
};
